import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { mediaList, mediaUpload } from "@/lib/api";
import { toast } from "sonner";
import { Star, UploadSimple } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SOURCE_LABEL = { client_uploaded: "Your photo", ai_generated: "AI image", product: "Product",
  branded_graphic: "Branded", stock: "Stock", imported: "Imported" };

export function MediaPicker({ clientId, open, onClose, onSelect, title = "Choose from Media Library" }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [rights, setRights] = useState(false);
  const fileRef = useRef();
  const listQ = useQuery({
    queryKey: ["media", clientId, q],
    queryFn: () => mediaList(clientId, q ? { q } : {}),
    enabled: !!clientId && open,
  });
  const assets = listQ.data?.assets || [];

  const quickUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!rights) { toast.error("Please confirm you own these photos first"); return; }
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("rights_confirmed", "true");
    files.forEach((f) => form.append("files", f));
    try {
      const r = await mediaUpload(form);
      const ok = r.results.filter((x) => x.ok).length;
      const bad = r.results.filter((x) => !x.ok);
      if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added to your library`);
      bad.forEach((b) => toast.error(`${b.filename}: ${b.error}`));
      qc.invalidateQueries({ queryKey: ["media", clientId] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-[#111116] border-white/10 text-white max-h-[85vh] overflow-y-auto" data-testid="media-picker">
        <DialogTitle className="text-white">{title}</DialogTitle>
        <DialogDescription className="sr-only">Pick a photo from your media library or upload a new one</DialogDescription>
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Search your photos…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-60 h-9" data-testid="media-picker-search" />
          <label className="flex items-center gap-1.5 text-[11px] text-[#9a9a9a] ml-auto cursor-pointer">
            <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)}
              className="accent-[#FF5E00]" data-testid="media-picker-rights" />
            I own these photos / have permission
          </label>
          <Button size="sm" variant="outline" className="border-white/15 h-9" data-testid="media-picker-upload-btn"
            onClick={() => fileRef.current?.click()}>
            <UploadSimple size={14} className="mr-1" /> Upload new
          </Button>
          <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic" hidden onChange={quickUpload} />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mt-1" data-testid="media-picker-grid">
          {assets.map((a) => (
            <button key={a.id} data-testid={`media-pick-${a.id}`} onClick={() => onSelect(a)}
              className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-[#FF5E00] focus:border-[#FF5E00] transition-colors text-left">
              <img src={`${BACKEND}${a.thumb_path || a.path}`} alt={a.alt_text || a.title}
                className="w-full h-24 object-cover" loading="lazy" />
              {a.favorite && <Star size={12} weight="fill" className="absolute top-1 right-1 text-amber-400" />}
              <div className="absolute bottom-0 inset-x-0 bg-black/70 px-1.5 py-1">
                <div className="text-[10px] truncate">{a.title}</div>
                <div className="text-[8px] uppercase tracking-wide text-[#9a9a9a]">{SOURCE_LABEL[a.source] || a.source}</div>
              </div>
            </button>
          ))}
          {!assets.length && !listQ.isLoading && (
            <div className="col-span-full text-sm text-[#9a9a9a] py-6 text-center">
              No photos yet — upload your first real business photo above.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
